import {
  GOOGLE_MAPS_ERRORS_MESSAGE,
  GTM_ACTIONS,
  GTM_BASE,
  GTM_CONSENT_ACTION,
  GTM_ERRORS,
  GTM_FEATURES,
  GTM_INPUT_ALERTS,
  GTM_PARAMS,
  GTM_PAYLOAD,
  KEYCLOAK_ADDRESS_KEYS,
  PNP_TRUSTLY_DECLINED_REASONS,
} from './constants';
import { resolveDeclinedReason } from './declinedReason.ts';
import { getGlobalGtmParams } from './globalParams';
import { TKeycloakUser } from './types';
import { TDynamicStoreField } from '../../store/store';
import { TRegistrationDoneGTM } from '../../types/types';
import { MARKETS_CODES } from '../countries';
import { REACT_APP_VERSION } from '../envConstants';
import { gatekeeper } from '../gatekeeper';

export class GtmEvents {
  static sendGtmEvent(feature: string, action: string, data: object = {}) {
    gatekeeper?.messageBus?.publish(GTM_BASE.TOPIC, {
      kind:  GTM_BASE.KIND,
      names: {
        feature,
        action,
      },
      data: {
        ...getGlobalGtmParams(),
        ...data,
      },
    });
  }

  static handleUserRegistrationEvents(user: TKeycloakUser) {
    if (gatekeeper.localisation.systemMarketSlug === MARKETS_CODES.se_sv)
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.BANK_ID_REDIRECT);
    if (!user) {
      return;
    }
    if (!user.is_fully_registered) {
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.REGISTRATION_START, {
        variant: `${gatekeeper.localisation.marketSlug}-${REACT_APP_VERSION}`,
      });
    }
    if (!user.address_is_verified) {
      const missingFields = Object.keys(user).reduce<string[]>((accumulator, key) => {
        if (KEYCLOAK_ADDRESS_KEYS.includes(key) && !user[key as keyof TKeycloakUser]) {
          return [...accumulator, key];
        }

        return accumulator;
      }, []);
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.FAIL, {
        errorCode:    GTM_ERRORS.NO_ADDRESS,
        error_detail: missingFields?.toString(),
      });
    } else {
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.ADDRESS);
    }
  }

  static handleWelcomeOfferEvent(woId: string | undefined | null, step: string) {
    if (woId) {
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.WELCOME_OFFER, {
        wo_used: GTM_PAYLOAD.TRUE,
        wo_id:   woId,
        step,
      });
    } else {
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.WELCOME_OFFER, {
        wo_used: GTM_PAYLOAD.FALSE,
        wo_id:   undefined,
        step,
      });
    }
  }

  static logPlacesApiRequestSent(searchQuery: string, timeStart: number) {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PLACES_API_REQUEST_SENT, {
      timestamp:  timeStart,
      endpoint:   GTM_PAYLOAD.NEAR_BY_SEARCH,
      parameters: searchQuery,
      URL:        window.location.href,
    });
  }

  static logPlacesApiResponseReceived(
    searchQuery: string,
    timeStart: number,
    status: google.maps.places.PlacesServiceStatus,
    response: unknown,
  ) {
    const currentTime = new Date().getTime();
    this.sendGtmEvent(
      GTM_FEATURES.REGISTER,
      GTM_ACTIONS.PLACES_API_RESPONSE_RECEIVED,
      {
        timestamp:    currentTime,
        responseTime: currentTime - timeStart,
        status:       status,
        response:     response,
        endpoint:     GTM_PAYLOAD.NEAR_BY_SEARCH,
        parameters:   searchQuery,
        URL:          window.location.href,
      },
    );
  }

  static logPlacesApiError(searchQuery: string, status: google.maps.places.PlacesServiceStatus) {
    const currentTime = new Date().getTime();
    this.sendGtmEvent(
      GTM_FEATURES.REGISTER,
      GTM_ACTIONS.PLACES_API_RESPONSE_RECEIVED,
      {
        timestamp:  currentTime,
        status:     status,
        endpoint:   GTM_PAYLOAD.NEAR_BY_SEARCH,
        parameters: searchQuery,
        URL:        window.location.href,
      },
    );
  }

  static logDepositLimits = (depositLimitDefined: boolean, limitType?: string) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.DEPOSIT_LIMITS, {
      event:                              `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.DEPOSIT_LIMITS}`,
      step:                               GTM_ACTIONS.DEPOSIT_LIMITS,
      [GTM_PARAMS.DEPOSIT_LIMIT_DEFINED]: depositLimitDefined,
      limit_type:                         limitType,
      userStatus:                         GTM_PAYLOAD.LOGGED_OUT,
      variant:                            `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logCountrySelector(step: string) {
    const gtmAction =
      step.includes('nationality') ?
        GTM_ACTIONS.SHOW_NATIONALITY_DRAWER :
        GTM_ACTIONS.SHOW_COUNTRY_OF_RESIDENCE_DRAWER;

    this.sendGtmEvent(
      GTM_FEATURES.REGISTER,
      gtmAction,
      {
        step,
      },
    );
  }

  static logMarketingConsent(consent: boolean) {
    const payload: { marketing_consent: boolean; step: string } = {
      marketing_consent: consent,
      step:              GTM_ACTIONS.MARKETING_CONSENT,
    };
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.MARKETING_CONSENT, payload);
  }

  static logMarketingConsentByVertical({
    marketing_channel_casino,
    marketing_channel_sports,
  }: { marketing_channel_casino?: string; marketing_channel_sports?: string }) {
    const payload: {
      marketing_consent: boolean;
      step: string;
      marketing_channel_casino?: string;
      marketing_channel_sports?: string;
    } = {
      marketing_consent: Boolean(marketing_channel_casino || marketing_channel_sports),
      step:              GTM_ACTIONS.MARKETING_CONSENT,
      marketing_channel_casino,
      marketing_channel_sports,
    };
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.MARKETING_CONSENT, payload);
  }

  static logRegistrationComplete(data: TRegistrationDoneGTM, action: string) {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, action, {
      ...data,
      variant: `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logRegistrationClosed() {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.REGISTRATION_CLOSED);
  }

  static setRegistrationClosedWithErrorGtmEvent = (failedAttempts: number, userValues: TDynamicStoreField) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.REGISTRATION_CLOSED, { failedAttempts, userValues });
  };

  static logRegistrationStart() {
    if (gatekeeper.localisation.systemMarketSlug === MARKETS_CODES.se_sv)
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.BANK_ID_REDIRECT);
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.REGISTRATION_START, {
      variant: `${gatekeeper.localisation.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logWoTerms = (step: string) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.SHOW_BONUS_TERMS, {
      step,
    });
  };

  static logAlert = (alert: string | object) => {
    if (typeof alert === 'object') {
      this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.ALERT, alert);

      return;
    }

    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.ALERT, {
      alert,
    });
  };

  static logInputAlert = (name: string, step: string) => {
    this.logAlert({ alert: GTM_INPUT_ALERTS[name as keyof typeof GTM_INPUT_ALERTS], step });
  }

  static logSearchAddressSent = (
    searchQuery: string,
    timeStart: number,
  ) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PLACES_API_REQUEST_SENT, {
      timestamp:  timeStart,
      endpoint:   GTM_PAYLOAD.NEAR_BY_SEARCH,
      parameters: searchQuery,
      URL:        window.location.href,
    });
  };

  static logSearchAddressReceived = (
    searchQuery: string,
    timeStart: number,
    status: google.maps.places.PlacesServiceStatus,
    response: unknown,
  ) => {
    const currentTime = new Date().getTime();
    this.sendGtmEvent(
      GTM_FEATURES.REGISTER,
      GTM_ACTIONS.PLACES_API_RESPONSE_RECEIVED,
      {
        timestamp:    currentTime,
        responseTime: currentTime - timeStart,
        status:       status,
        response:     response,
        endpoint:     GTM_PAYLOAD.NEAR_BY_SEARCH,
        parameters:   searchQuery,
        URL:          window.location.href,
      },
    );
  };

  static logSearchAddressError = (
    searchQuery: string,
    status: google.maps.places.PlacesServiceStatus,
  ) => {
    const currentTime = new Date().getTime();
    this.sendGtmEvent(
      GTM_FEATURES.REGISTER,
      GTM_ACTIONS.PLACES_API_RESPONSE_RECEIVED,
      {
        timestamp:    currentTime,
        errorCode:    status,
        errorMessage: GOOGLE_MAPS_ERRORS_MESSAGE[status],
        endpoint:     GTM_PAYLOAD.NEAR_BY_SEARCH,
        parameters:   searchQuery,
        URL:          window.location.href,
      },
    );
  };

  static logSelectAddress = (selectedAddress: string) => {
    const currentTime = new Date().getTime();
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PLACES_SELECTED_ADDRESS, {
      timestamp: currentTime,
      selected:  selectedAddress,
      endpoint:  GTM_PAYLOAD.NEAR_BY_SEARCH,
      URL:       window.location.href,
    });
  };

  static logTerms = (type: string, step: string) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_CONSENT_ACTION[type as keyof typeof GTM_CONSENT_ACTION], {
      step,
    });
  }

  static wrongFormatIdentificationNumberEs = () => {
    this.logAlert({ alert: 'WRONG_IDENTIFICATION_NUMBER_FORMAT', step: 'identification_number' });
  }

  static logPnpStartScreen = () => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PNP_START_SCREEN, {
      event:      `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.PNP_START_SCREEN}`,
      userStatus: GTM_PAYLOAD.LOGGED_OUT,
      variant:    `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logPnpTrustlyClickStart = (amount: string | number) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PNP_TRUSTLY_CLICK_START, {
      event:              `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.PNP_TRUSTLY_CLICK_START}`,
      initiated_from:     GTM_PAYLOAD.REGISTRATION_FLOW,
      pnp_deposit_amount: amount,
      userStatus:         GTM_PAYLOAD.LOGGED_OUT,
      variant:            `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logPnpTrustlyDone = (accepted: boolean, declinedReason?: string) => {
    const reason = accepted
      ? PNP_TRUSTLY_DECLINED_REASONS.NOT_APPLICABLE
      : resolveDeclinedReason(declinedReason);

    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PNP_TRUSTLY_DONE, {
      event:                       `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.PNP_TRUSTLY_DONE}`,
      transaction_accepted:        accepted,
      transaction_declined_reason: reason,
      userStatus:                  GTM_PAYLOAD.LOGGED_OUT,
      variant:                     `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logPnpAutoLoginDone = (accepted: boolean, declinedReason?: string) => {
    const reason = accepted
      ? PNP_TRUSTLY_DECLINED_REASONS.NOT_APPLICABLE
      : declinedReason || 'pnp_login_failed';

    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PNP_AUTO_LOGIN_DONE, {
      event:                 `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.PNP_AUTO_LOGIN_DONE}`,
      login_accepted:        accepted,
      login_declined_reason: reason,
      userStatus:            GTM_PAYLOAD.LOGGED_OUT,
      variant:               `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }

  static logPnpRegistrationFailedCallback = (
    { status, refundAmount, failedAttempts }:
    { status?: number; refundAmount?: string | number | null; failedAttempts?: number },
  ) => {
    this.sendGtmEvent(GTM_FEATURES.REGISTER, GTM_ACTIONS.PNP_REGISTRATION_FAILED_CALLBACK, {
      event:            `fabric.${GTM_FEATURES.REGISTER}.${GTM_ACTIONS.REGISTRATION_FAILED}`,
      step:             GTM_ACTIONS.REGISTRATION_FAILED,
      status,
      refund_amount:    refundAmount,
      refund_timestamp: new Date().toISOString(),
      failedAttempts,
      userStatus:       GTM_PAYLOAD.LOGGED_OUT,
      variant:          `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
    });
  }
}
